
import {
    createContext,
    useContext,
    useEffect,
    Dispatch,
    useReducer,
    PropsWithChildren
} from 'react';
import { isDesktop } from 'react-device-detect';

import IAppState from '../models/IAppState';
import reducer from './reducers';
import ActionType from '../models/ActionType';
import IAuth from '../models/IAuth';
import { authApi } from '../api/authApi';
import { fetchUserPoints } from '../api/minBetsApi';
import { fetchClientConfig } from '../api/configApi';
import { hydrateTokens } from '../api/tokenStore';
import { registerOAuthDeepLinkListener } from '../api/nativeOAuth';
import createInitialAppState, { initialAppState } from './createInitialAppState';

interface IAppContext {
    state: IAppState,
    dispatch: Dispatch<ActionType>,
}
const AppStateContext = createContext<IAppContext>({
    state: initialAppState,
    dispatch: () => { },
});

function createAuthWithDispatch(dispatch: Dispatch<ActionType>): IAuth {
    return {
        authUser: null,
        isAuthenticated: false,
        isLoading: false,
        signOut: async () => {
            try {
                await authApi.logout();
            } catch {
                // Network error (e.g. offline) — still clear local state.
            }
            dispatch({ type: 'AUTH_LOGOUT' });
        },
        signInWithEmail: async (email, password) => {
            dispatch({ type: 'AUTH_SET_LOADING', isLoading: true });
            const result = await authApi.login(email, password);
            if (result.user) {
                dispatch({ type: 'AUTH_LOGIN_SUCCESS', user: result.user });
            } else {
                dispatch({ type: 'AUTH_SET_LOADING', isLoading: false });
            }
            return result;
        },
        signInWithGoogle: async () => {
            authApi.oauthLogin('google');
            return { user: null };
        },
        signInWithFacebook: async () => {
            authApi.oauthLogin('facebook');
            return { user: null };
        },
        resetPassword: async (email) => {
            await authApi.requestPasswordReset(email);
        },
        signUpUserWithEmailAndPassword: async (email, password, displayName) => {
            dispatch({ type: 'AUTH_SET_LOADING', isLoading: true });
            const result = await authApi.register(email, password, displayName);
            dispatch({ type: 'AUTH_SET_LOADING', isLoading: false });
            return result;
        },
    };
}

export function AppStateProvider({ children }: PropsWithChildren<{}>) {
    const placeholder: IAuth = {
        authUser: null,
        isAuthenticated: false,
        isLoading: false,
        signOut: async () => { },
        signInWithEmail: async () => ({ user: null }),
        signInWithGoogle: async () => ({ user: null }),
        signInWithFacebook: async () => ({ user: null }),
        resetPassword: async () => { },
        signUpUserWithEmailAndPassword: async () => { },
    };
    const fullInitialAppState = { ...createInitialAppState(placeholder) };
    const [state, dispatch] = useReducer(reducer, fullInitialAppState);

    // Restore session on mount (handles page refresh). If we already hydrated
    // a cached user from localStorage, only clear them on an explicit failure
    // *while online* — an offline /api/me failure should keep the cached
    // identity so the PWA doesn't appear logged out.
    useEffect(() => {
        // On native, load persisted Bearer tokens before the first authenticated
        // request. No-op on web (cookie-based auth).
        hydrateTokens().then(() => authApi.getMe()).then(user => {
            if (user) {
                dispatch({ type: 'AUTH_LOGIN_SUCCESS', user });
            } else if (typeof navigator === 'undefined' || navigator.onLine) {
                dispatch({ type: 'AUTH_LOGOUT' });
            }
        }).catch(() => {
            // Network error — keep any cached user; only sign out if online.
            if (typeof navigator !== 'undefined' && navigator.onLine) {
                dispatch({ type: 'AUTH_LOGOUT' });
            }
        });
    }, []);

    // Fetch public client config (password rules, proximity radius, ...) once on mount.
    useEffect(() => {
        fetchClientConfig().then(config => {
            dispatch({ type: 'CLIENT_CONFIG_SET', config });
        });
    }, []);

    // Complete native OAuth: the system browser redirects back via the
    // crapapp:// deep link; on success persist tokens and load the user.
    useEffect(() => {
        let handle: { remove: () => void } | null = null;
        registerOAuthDeepLinkListener(async (outcome) => {
            if (outcome.ok) {
                dispatch({ type: 'AUTH_SET_LOADING', isLoading: true });
                const user = await authApi.getMe();
                if (user) {
                    dispatch({ type: 'AUTH_LOGIN_SUCCESS', user });
                } else {
                    dispatch({ type: 'AUTH_SET_LOADING', isLoading: false });
                }
            } else {
                dispatch({
                    type: 'OK_DIALOG_OPEN_FULL_UPDATE',
                    open: true,
                    title: 'Sign in failed',
                    text: 'We could not complete sign in. Please try again.',
                });
            }
        }).then(h => { handle = h; });
        return () => { handle?.remove(); };
    }, []);

    // Refresh user points whenever authentication state becomes true.
    useEffect(() => {
        if (!state.auth?.isAuthenticated) {
            dispatch({ type: 'USER_POINTS_CLEAR' });
            return;
        }
        fetchUserPoints().then(points => {
            if (points) dispatch({ type: 'USER_POINTS_SET', points });
        });
    }, [state.auth?.isAuthenticated]);

    // Create auth with real dispatch-backed methods
    const auth = createAuthWithDispatch(dispatch);

    // Merge persisted auth state (authUser, isAuthenticated) with dispatch-backed methods
    const mergedAuth: IAuth = state.auth ? {
        ...auth,
        authUser: state.auth.authUser,
        isAuthenticated: state.auth.isAuthenticated,
        isLoading: state.auth.isLoading,
    } : auth;

    const safeState: IAppState = { ...state, auth: mergedAuth };
    safeState.isOnMobileDevice = !isDesktop;
    return <AppStateContext.Provider value={{ state: safeState, dispatch }}>{children}</AppStateContext.Provider>;
}

export const useAppState = () => useContext(AppStateContext);